"use strict";

import Vector from "ol/source/Vector";
import Collection from 'ol/Collection';
import * as olLoadingstrategy from 'ol/loadingstrategy';
import Feature from 'ol/Feature';
import {fromExtent} from 'ol/geom/Polygon';

/**
 * Namespace für Source.
 *
 * @namespace
 * @memberof gc.gis
 */
gc.gis.source = gc.gis.source || {};

/**
 * @classdesc
 * Openlayers Implementierung einer Datenquelle für den Zugriff auf Vektorgeometrien des Kartenkatalogs.
 *
 * @class
 * @memberof gc.gis.source
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCMapView} map aktuelle Karte, auf deren Bezug die Vektordaten geladen werden sollen
 * @param {Object} [opt_options] optionale Aufrufoptionen
 * @param {string} [opt_options.loadMessage] Meldung die beim Laden angezeigt werden soll
 * @param {gc.gis.layer.GCAbstractLayer[]|undefined} [opt_options.sourceLayers=undefined] zu berücksichtigende Layer
 *          wenn undefined werden alle sichtbaren Layer berücksichtigt
 * @param {boolean} [opt_options.forceMapProjection=false] berücksichtigt beim Laden nur Layer, deren Projektion mit der
 *          der aktuellen Karte übereinstimmen
 * @param {boolean} [opt_options.ignoreSupportFeatureList=false] Flag, ob die Layer-Einstellung
 *          "Layer bei 'GetFeatureList' berücksichtigen" ignoriert werden soll. Dies ist z.B. bei der
 *          Bearbeitunsgfunktion sinnvoll (Default: false)
 * @param {boolean} [opt_options.returnWritableOnly=false] Flag, ob nur Objekte mit Schreibberechtigung berücksichtigt
 *          werden sollen. Dies ist z.B. bei der Bearbeitunsgfunktion sinnvoll (Default: false)
 * @param {Function} [opt_options.load] Methode die aufgerufen wird, wenn ein Feature geladen wurde. Diese
 *          Funktion kann genutzt werden um die Features anzureichern oder in eignen Modulen weiter zu verarbeiten.
 *          Diese Funktion muss <code>true</code> zurückgeben, damit das Feature in die Ergebnismenge aufgenommen wird.
 * @param {Function} [opt_options.clearExcludes] Methode die aufgerufen wird, bevor die Datenquelle geleert wird. Diese
 *          Funktion kann genutzt werden um eine Menge von Features beizubehalten, z.B. da diese sich gerade in
 *          Bearbeitung befinden. Sofern diese Methode eine Menge zurückliefert, ist der Löschmechanismus beim
 *          Nachladen von Features recht zeititensiv.
 */
gc.gis.source.OLMapCatalogueVector = function(map, opt_options)
{
	opt_options = opt_options || {};

	/**
	 * @type {gc.gis.map.GCMapView}
	 * @private
	 */
	this._map = map;

	/**
	 * @type {ol.Map}
	 * @private
	 */
	this._ol_map = this._map.getMap().getImpl();

	/**
	 * @type {string}
	 * @private
	 */
	this._loadMessage = opt_options.loadMessage || 'Lade Vektordaten ...';

	/**
	 * Auflösung, welche bei der letzten Änderung des Viewports eingestellt wurde.
	 * @type {number}
	 * @private
	 */
	this._lastResolution = -1;

	/**
	 *
	 * @type {gc.gis.layer.GCAbstractLayer[]|undefined}
	 * @private
	 */
	this._sourceLayers = opt_options.sourceLayers;

	/**
	 * @type {boolean}
	 * @private
	 */
	this._ignoreSupportFeatureList = opt_options.ignoreSupportFeatureList === true;

	/**
	 * @type {boolean}
	 * @private
	 */
	this._returnWritableOnly = opt_options.returnWritableOnly === true;

	/**
	 * @type {boolean}
	 * @private
	 */
	this._forceMapProjection = opt_options.forceMapProjection === true;

	/**
	 * @type {Function}
	 * @private
	 */
	this._loadFunc = opt_options.load;

	/**
	 * @type {Function}
	 * @private
	 */
	this._clearExcludes = opt_options.clearExcludes;

	/**
	 * @type {ol.source.Vector}
	 * @private
	 */
	this._ol_source = undefined;

	/**
	 * @type {Array}
	 * @private
	 */
	this._loadedExtents = [];

	this._initSource();
	this._initEvents();
};

/**
 * Liefert das Openlayers Source-Objekt.
 *
 * @return {ol.source.Vector}
 */
gc.gis.source.OLMapCatalogueVector.prototype.getSource = function()
{
	return this._ol_source;
};


/**
 * Veranlasst das Neuladen der Daten für der Source.
 *
 * @private
 */
gc.gis.source.OLMapCatalogueVector.prototype.refresh = function()
{
	if (this._clear() > 0)
	{
		// diese Variante behält die Objekte der Source beim Neuladen bei
		while (this._loadedExtents.length)
		{
			this._ol_source.removeLoadedExtent(this._loadedExtents.pop());
		}
	}
	else
	{
		this._loadedExtents = [];
	}

	this._ol_source.refresh();
};

/**
 * Setzt die vorgegebenen Quell-Layer.
 *
 * @param {gc.gis.layer.GCAbstractLayer[]|gc.gis.layer.GCAbstractLayer} layer
 */
gc.gis.source.OLMapCatalogueVector.prototype.setSourceLayers = function(layer)
{
	if (layer instanceof gc.gis.layer.GCAbstractLayer)
	{
		this._sourceLayers = [layer];
	}
	else if (layer === null || layer === undefined)
	{
		this._sourceLayers = undefined;
	}
	else
	{
		this._sourceLayers = layer;
	}
};

/**
 * Setzt die vorgegebenen Quell-Layer zurück (ab nun werden alle sichtbaren Layer berücksichtig).
 */
gc.gis.source.OLMapCatalogueVector.prototype.resetSourceLayers = function()
{
	this._sourceLayers = undefined;
};

/**
 * Initialisiert die Events der Vektor-Source.
 *
 * @private
 */
gc.gis.source.OLMapCatalogueVector.prototype._initEvents = function()
{
	const self = this;
	let i, ii;

	// Ebenen überwachen
	this._map.on(gc.gis.map.GCMapEvent.LAYER_ADDED, self, function (evt) {
		if(evt.layers && evt.layers.length)
		{
			for(i = 0, ii = evt.layers.length; i < ii; i++)
			{
				if (evt.layers[i] instanceof gc.gis.layer.GCLayer && evt.layers[i].isReallyVisible())
				{
					self.refresh();
				}

				evt.layers[i].on(gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, self, function(evt) {
					self.refresh();
				});
			}
		}
	});

	this._map.on(gc.gis.map.GCMapEvent.LAYER_REMOVED, self, function (evt) {
		if(evt.layers && evt.layers.length)
		{
			for (i = 0, ii = evt.layers.length; i < ii; i++)
			{
				evt.layers[i].un(gc.gis.layer.GCLayerEvent.VISIBILITY_CHANGED, self);
				self.refresh();
			}
		}
	});

	// Auflösung (Zoom) überwachen
	this._map.on(gc.gis.map.GCMapEvent.VIEWPORT_CHANGEEND, self, function (evt) {
		const res = self._ol_map.getView().getResolution();
		// !! nur beim Hereinzoomen - beim Herauszoomen setzt OL schon automatisch einen Request ab, wenn das Extent noch nicht geladen wurde
		if (self._lastResolution > res)
		{
			// Beim Hereinzoomen einen Refresh der Datenquelle erzwingen.
			// Hintergrund: Die LoadingStrategy BBOX würde beim Hereinzoomen erkennen, dass der aktuelle
			// Kartenausschnitt in einer bereits geladenen BBOX enthalten ist und damit einen Refresh verhindern.
			// Prinzipiel ist das prima, allerdings macht dieser Mechanismus Probleme mit der Layer-Eigenschaft
			// "Layer bei "GetFeatureList" berücksichtigen" in Kombination mit min./max. Maßstäben
			self.refresh();
		}
		self._lastResolution = res;
	});
};

/**
 * Leert die Datenquelle und liefert die Anzahl der geschützten Objekte (d.h. Objekte, die durch einen Exclude-Filter
 * vom Löschen ausgeschlossen wurden).
 *
 * @private
 * @return {Number}
 */
gc.gis.source.OLMapCatalogueVector.prototype._clear = function()
{
	const self = this;
	let excludes = [],
		excludeIdMap = {},
		i, cnt = 0;

	if (typeof this._clearExcludes === 'function')
	{
		excludes = this._clearExcludes.call(this);
		for (i = excludes.length - 1; i >= 0; i--)
		{
			excludeIdMap[excludes[i].getID()] = true;
		}
	}

	if (excludes.length > 0)
	{
		this._ol_source.forEachFeature(function (feature) {
			if (excludeIdMap[feature.getId()] === undefined)
			{
				self._ol_source.removeFeature(feature);
			}
			else
			{
				cnt++;
			}
		});
	}
	else
	{
		// clear führt keinen Reload der Daten aus
		this._ol_source.clear();
	}

	return cnt;
};

/**
 * Liefert die relevanten Layer für die Vektordatenabfrage.
 *
 * @return {gc.gis.layer.GCAbstractLayer[]}
 * @private
 */
gc.gis.source.OLMapCatalogueVector.prototype._getRelevantLayers = function()
{
	// TODO die sichtbaren Maßstabsbereiche berücksichtigen um Anfragen zu reduzieren
	let layers = [];
	let i, ii;

	if (Array.isArray(this._sourceLayers))
	{
		layers = this._sourceLayers;
	}
	else
	{
		layers = this._map.getLayersRecursive(function(layer) {
			return layer.isReallyVisible()
				&& layer instanceof gc.gis.layer.GCLayer
				&& layer.isInternal()
				&& layer.getType() !== gc.gis.layer.GCLayerType.INTERNAL_WMS;
		});
	}

	if (this._forceMapProjection)
	{
		const tmp = [];
		for (i = 0, ii = layers.length; i < ii; i++)
		{
			if (layers[i] instanceof gc.gis.layer.GCLayer && layers[i].getProjection() === this._map.getProjection())
			{
				tmp.push(layers[i]);
			}
		}
		layers = tmp;
	}

	return layers;
};

/**
 * Initialisiert die Datenquelle (Loader) der Vektor-Source.
 *
 * @private
 */
gc.gis.source.OLMapCatalogueVector.prototype._initSource = function()
{
	const self = this;

	this._ol_source = new Vector({
		features: new Collection(),      // If provided as Collection, the features in the source and the collection will stay in sync.
		strategy: olLoadingstrategy.bbox,
		useSpatialIndex: true
	});

	this._ol_source.setLoader(function (extent, resolution, projection)
	{
		// !!! das übergebene Extent enthält noch einen Offset (unten/oben und links/rechts)
		// => damit ist es Größer als der tatsächlich sichtbare Bereich
		const viewportExtent = self._map.getViewportExtent();
		self._loadedExtents.push(extent);

		const onLoad = function (serviceFeature, olFeature) {
			if (typeof self._loadFunc === 'function')
			{
				if (self._loadFunc.call(self, serviceFeature, olFeature))
				{
					self._ol_source.addFeature(olFeature);
				}
			}
			else
			{
				self._ol_source.addFeature(olFeature);
			}
		};

		const onError = function ()
		{
			self._ol_source.removeLoadedExtent(extent);
		};

		const onSuccess = function (result)
		{
			if (result.features)
			{
				let feature, geomList, ol_feature, uniqueId;
				for (let j = 0, jj = result.features.length; j < jj; j++)
				{
					feature = result.features[j];

					if (feature instanceof gc.gis.service.feature.QueryFachDBFeature)
					{
						// Koordinatenbezüge "Flachmachen" und in OL Features mit passenden Transaktions-Feature pressen
						geomList = feature.getGeometryList();
						for (let k = 0, kk = geomList.length; k < kk; k++)
						{
							if (geomList[k].geometry instanceof gc.gis.geom.GCGeometry)
							{
								// TODO doch besser serverseitig
								geomList[k].geometry.project(self._map.getProjection());
								uniqueId = feature.getFeatureLayer() + '.' + feature.getID() + '#' + geomList[k].ID;

								ol_feature = new Feature();
								ol_feature.setId(uniqueId);
								ol_feature.setGeometry(geomList[k].geometry.getImpl());
								onLoad(feature, ol_feature);
							}
						}
					}
					else
					{
						if (feature.getGeometry() instanceof gc.gis.geom.GCGeometry)
						{
							// TODO doch besser serverseitig
							feature.getGeometry().project(self._map.getProjection());

							ol_feature = new Feature();
							ol_feature.setId(feature.getFeatureLayer() + '.' + feature.getID());
							ol_feature.setGeometry(feature.getGeometry().getImpl());
							onLoad(feature, ol_feature);
						}
					}
				}
			}
		};

		const layers = self._getRelevantLayers();
		// Bereits geladene Features entfernen, es werden beim Reload immer alle im aktuellen Ausschnitt geladen.
		// Liefert ein Layer keine Objekte, z.B. weil er nicht mehr im sichtbaren Masstabsbereich liegt,
		// dürfen die bereits geladenen aber auch nicht mehr zum fangen benutzt werden. OL leert die Datenquelle
		// nämlich nicht automatisch, sondern fügt nur hinzu. Über die FeatureID wird geprüft, ob das Objekt bereits
		// in der Datenquelle enthalten ist.
		self._clear();

		for (let i = 0, ii = layers.length; i < ii; i++)
		{
			if (!(layers[i] instanceof gc.gis.layer.GCLayer))
			{
				continue;
			}

			if (!layers[i].isInternal())
			{
				continue;
			}

			const service = gc.gis.iface.serviceFactory.createQueryService();
			service.on(gc.gis.service.FeatureServiceEvent.QUERY_MAP_FEATURES_SUCCESS, this, function (evt) {
				onSuccess(evt);
			});
			service.on(gc.gis.service.FeatureServiceEvent.QUERY_MAP_FEATURES_ERROR, this, function (evt) {
				onError();
			});

			const geom = gc.gis.iface.mapFactory.createPolygon(fromExtent(extent));
			geom.setCRS(projection.getCode());

			service.queryMapFeatures(
				layers[i],
				geom,
				viewportExtent,
				self._map.getSize(),
				{
					returnGeometry: true,
					returnIdsOnly: true,
					returnWritableOnly: self._returnWritableOnly,
					ignoreSupportFeatureList: self._ignoreSupportFeatureList,
					message: self._loadMessage
				}
			);
		}
	});
};